import { createMuiTheme } from "@material-ui/core";
import { softShadows } from "./defaultTheme";

/** Sidebar/chrome colors for the pre-owned admin. Read by PreownedSidebar. */
export const preownedPalette = {
  slate: "#1E232B",
  slateLight: "#2A313C",
  amber: "#E0A030",
  amberDim: "rgba(224,160,48,0.16)",
  // Checkboxes and links stay conventional blue — the slate primary reads as
  // near-black on a tick, which is heavier than a checkbox should look.
  blue: "#1565c0",
};

/**
 * Theme for the pre-owned admin. Deliberately unlike the main admin's blue
 * chrome so there's no mistaking which side you're editing on: dark slate
 * primary, amber secondary, warm off-white page background.
 *
 * Structural overrides (radii, font sizes, input props) intentionally mirror
 * defaultTheme so forms keep the same shape — only the colors differ.
 */
export const preownedTheme = createMuiTheme({
  palette: {
    type: "light",
    primary: { main: preownedPalette.slate, contrastText: "#fff" },
    secondary: {
      main: preownedPalette.amber,
      contrastText: "#1E232B",
      dark: "#B87F1E",
      light: "#F3C46B",
    },
    success: { main: "#4CAF50", contrastText: "#fff" },
    info: { main: preownedPalette.slate, contrastText: "#fff" },
    warning: { main: "#ffb400", contrastText: "#000" },
    error: { main: "#ff0000", contrastText: "#fff" },
    background: { default: "#FAF7F2", paper: "#fff" },
  },
  overrides: {
    MuiCheckbox: {
      // Scoped to the checkbox's own icon — a bare MuiSvgIcon override would
      // shrink every icon in the admin, including the sidebar chevrons.
      root: {
        "& .MuiSvgIcon-root": { width: "0.9em", height: "0.9em" },
      },
      colorPrimary: {
        "&$checked": { color: "#1565c0" },
      },
    },
    MuiFormControlLabel: {
      label: {
        fontSize: "12px",
      },
    },
    MuiAutocomplete: {
      option: {
        '&[data-focus="true"]': {
          backgroundColor: preownedPalette.amberDim,
          color: preownedPalette.slate,
          fontSize: 12,
        },
        fontSize: 12,
      },
    },
    MuiInputBase: {
      root: {
        borderRadius: 3,
        fontSize: "0.875rem",
      },
      input: {
        fontSize: "0.875rem",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 3,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 3,
        fontSize: "13px",
      },
    },
    MuiAccordion: {
      root: {
        borderRadius: 3,
        boxShadow:
          "0 1px 3px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04)",
        "&$expanded": {
          borderRadius: 3,
          boxShadow:
            "0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 3,
      },
    },
    MuiChip: {
      root: {
        borderRadius: 3,
      },
    },
  },
  props: {
    MuiCheckbox: {
      color: "primary",
      disableRipple: true,
    },
    MuiTextField: {
      variant: "outlined",
      size: "small",
      InputLabelProps: {
        shrink: true,
      },
    },
  },
  // Everything that derives a corner from the theme rather than declaring one
  // — the outlined Select's inner element, filled inputs, menus — picks this
  // up, so inputs match the 3px used on buttons and panels.
  shape: { borderRadius: 3 },
  shadows: softShadows,
});